import PhoneMockup from "./PhoneMockup";
import { Button } from "@/components/ui/button";
import { MessageCircle, Star } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const heroAvatars = ["/images/avatar-1.webp", "/images/avatar-2.webp", "/images/avatar-3.webp"];

interface HeroProps {
  onCtaClick: () => void;
}

const Hero = ({ onCtaClick }: HeroProps) => {
  const { t, isRTL } = useLanguage();

  const heroMessages = [
    { type: "sent" as const, text: "", time: t("whatsapp.hero.time1"), isVoice: true },
    { type: "received" as const, text: t("whatsapp.hero.msg2"), time: t("whatsapp.hero.time2") },
    { type: "received" as const, text: t("whatsapp.hero.msg3"), time: t("whatsapp.hero.time3") },
  ];

  return (
    <section
      className="relative min-h-screen flex items-center overflow-hidden bg-background"
      dir={isRTL ? "rtl" : "ltr"}
    >
      <div className="absolute top-0 end-0 w-1/2 h-full rounded-s-[200px] -z-10 bg-primary/5" />

      <div className="container mx-auto px-4 py-20 lg:py-0">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="text-center lg:text-start animate-fade-in">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6 bg-primary/10 text-accent">
              <MessageCircle className="w-4 h-4" />
              <span>{t("hero.badge")}</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6 text-foreground">
              {t("hero.title")} <span className="text-gradient">{t("hero.titleHighlight")}</span>
            </h1>

            <p className="text-lg md:text-xl max-w-xl mx-auto lg:mx-0 mb-8 text-muted-foreground">
              {t("hero.subtitle")}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-4">
              <Button
                size="lg"
                className="text-lg px-10 py-7 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all hover:scale-105 bg-primary hover:bg-primary/90 text-primary-foreground"
                onClick={onCtaClick}
              >
                <span dir="auto">{t("hero.cta")}</span>
              </Button>
            </div>

            <div className="flex items-center gap-2 justify-center lg:justify-start mb-8">
              <div className="flex -space-x-2 rtl:space-x-reverse">
                {heroAvatars.map((avatar, i) => (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    key={i}
                    src={avatar}
                    alt={`User ${i + 1}`}
                    loading="lazy"
                    decoding="async"
                    className="w-8 h-8 rounded-full border-2 border-background object-cover"
                  />
                ))}
              </div>
              <p className="text-sm text-muted-foreground">
                <span className="font-semibold text-primary">1,247</span> {t("hero.socialProof")}
              </p>
            </div>

            <div className="flex items-center gap-2 justify-center lg:justify-start">
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <span className="text-sm text-muted-foreground">
                <span className="font-semibold text-foreground">4.9</span> {t("hero.rating")}
              </span>
            </div>
          </div>

          <div className="flex justify-center lg:justify-end animate-fade-in-up">
            <PhoneMockup messages={heroMessages} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
